/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

// @ts-ignore
/* eslint-disable no-duplicate-imports */
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { IChatContext, TContextProvider } from '@msdyn365-commerce-modules/chat';
import { IPvaChatConnectorViewProps } from '../../pva-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * CartAmountTrigger class - proactive chat is triggered if cArt amount is in preset boundaries (config.proactiveOnCartAmountMin, config.proactiveOnCartAmountMax)
 */
class CartAmountTrigger extends ProactiveChatTrigger {
    private min?: number;
    private max?: number;
    private totalInCart: number = 0;

    constructor(props: IPvaChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        this.min = this.props.config.proactiveOnCartAmountMin;
        this.max = this.props.config.proactiveOnCartAmountMax;
        this.props.context.telemetry.debug(
            `[Chat][${CartAmountTrigger.name}] Cart amount proactive chat initialized with min: '${this.min}'; max: '${this.max}'.`
        );
    }

    public async validate(): Promise<boolean> {
        const cart = await this._getCart();
        if (cart.isEmpty) {
            this.props.context.telemetry.debug(`[Chat][${CartAmountTrigger.name}] Cart was empty. Validation returning 'false'.`);
            return false;
        }

        this.totalInCart = cart.cart.TotalAmount || 0;
        let retVal = false;
        if (this.min) {
            if (this.max) {
                retVal = this.min <= this.totalInCart && this.totalInCart <= this.max;
            } else {
                retVal = this.min <= this.totalInCart;
            }
        } else {
            if (this.max) {
                retVal = this.totalInCart <= this.max;
            } else {
                retVal = false;
            }
        }
        this.props.context.telemetry.debug(
            `[Chat][${CartAmountTrigger.name}] Validation result: '${retVal}'; Cart total: '${this.totalInCart}'.`
        );
        return retVal;
    }

    public getContextProvider(): TContextProvider {
        return () => {
            return {
                ...this.context,
                eCommerceCustIsProactiveChat: 'True',
                eCommerceCustProactiveType: 'Cart Amount',
                eCommerceCustProactiveShoppingCartAmount: `${this.totalInCart}`
            };
        };
    }

    public getMessage(): string {
        // @TODO: Add message processing if needed
        return this.props.config.proactiveOnCartAmountMessage || this.props.config.proactiveDefaultMessage;
    }

    private async _getCart(): Promise<ICartState> {
        return getCartState(this.props.context.actionContext);
    }
}

export default CartAmountTrigger;
